import { HorizonShareItemRef, HorizonShareable } from '../../lead-horizon-tracking.model';

export type ShareSort = 'recent' | 'name' | 'type';

export interface ShareFolder {
  key: string;
  label: string;
  count: number;
}

/** One lot, Sharing Library document or library file in the share dialog, flattened so the list and grid views render them alike. */
export interface ShareRow {
  key: string;
  /** What goes to the share call: a lot, an approved Sharing Library item, or any file of the project's library. */
  type: HorizonShareItemRef['type'];
  id: string;
  name: string;
  thumbnail: string | null;
  icon: string;
  /** Upper-case file type for documents ("PDF"), "Lot" for lots. */
  typeLabel: string;
  /** Lots: level · beds · baths · cars. */
  detail: string | null;
  /** Lots: availability. */
  badge: string | null;
  price: number | null;
  sizeLabel: string | null;
  sizeBytes: number | null;
  updated: string | null;
  /** Document category; lots sit in their own folder. */
  folder: string;
  /** Position in the API response: the "Recently Added" order for rows that carry no date. */
  order: number;
  /** When the document was approved for sharing; "Recently Added" sorts on it, newest first. */
  addedAt: string | null;
  /** The library folder's position, so folders list in the library's own order. */
  categoryOrder: number;
  /** Lots: the floor plan shared along with the lot, as the share call takes it; null when none is linked. */
  floorplan: HorizonShareItemRef | null;
  /** Lots: the floor plan's name ("B"), for the "Include floor plan" option. */
  floorplanName: string | null;
}

type ShareableDocument = HorizonShareable['documents'][number] & { size_bytes?: number | null; updated_at?: string | null };
type ShareableLot = HorizonShareable['lots'][number];
type ShareableLibraryFile = NonNullable<HorizonShareable['library_files']>[number];

/** Rows that are files, not lots. */
export function isFileRow(row: ShareRow): boolean {
  return row.type !== 'lot';
}

const SIZE_UNITS: Record<string, number> = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };

/** Bytes from a label like "4.2 MB" or "612 KB"; null when it can't be read. */
export function parseSize(label: string | null | undefined): number | null {
  const match = /^\s*([\d.,]+)\s*(B|KB|MB|GB)\s*$/i.exec(label ?? '');
  if (!match) {
    return null;
  }
  const value = Number(match[1].replace(/,/g, ''));
  return Number.isFinite(value) ? value * SIZE_UNITS[match[2].toUpperCase()] : null;
}

export function formatBytes(bytes: number): string {
  if (bytes >= SIZE_UNITS['GB']) {
    return `${(bytes / SIZE_UNITS['GB']).toFixed(1)} GB`;
  }
  if (bytes >= SIZE_UNITS['MB']) {
    return `${(bytes / SIZE_UNITS['MB']).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / SIZE_UNITS['KB']))} KB`;
}

function fileIcon(fileType: string): string {
  const type = (fileType ?? '').toLowerCase();
  if (type === 'pdf') {
    return 'fa-file-pdf';
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(type)) {
    return 'fa-file-image';
  }
  if (['mp4', 'mov', 'avi', 'webm', 'm4v'].includes(type)) {
    return 'fa-file-video';
  }
  if (['doc', 'docx'].includes(type)) {
    return 'fa-file-word';
  }
  if (['xls', 'xlsx', 'csv'].includes(type)) {
    return 'fa-file-excel';
  }
  if (['zip', 'rar', '7z'].includes(type)) {
    return 'fa-file-zipper';
  }
  return 'fa-file-lines';
}

export function toDocumentRow(doc: ShareableDocument, order: number): ShareRow {
  return {
    key: `document:${doc.id}`,
    type: 'document',
    id: doc.id,
    name: doc.name ?? '',
    thumbnail: doc.thumbnail || null,
    icon: fileIcon(doc.file_type),
    typeLabel: (doc.file_type || 'File').toUpperCase(),
    detail: null,
    badge: null,
    price: null,
    sizeLabel: doc.size_label || null,
    sizeBytes: doc.size_bytes ?? parseSize(doc.size_label),
    updated: doc.updated_at ?? null,
    folder: doc.category_name?.trim() || 'Uncategorised',
    order,
    addedAt: doc.added_at ?? null,
    categoryOrder: doc.category_order ?? Number.MAX_SAFE_INTEGER,
    floorplan: null,
    floorplanName: null,
  };
}

/**
 * A file from the project's library (`/shareable` `library_files[]`) that is not an approved Sharing Library document;
 * the agent may still share it, as `{ type: 'library_file', id: library_file_id }`. `folder` is the library's own folder.
 */
export function toLibraryFileRow(file: ShareableLibraryFile, order: number): ShareRow {
  return {
    key: `library_file:${file.library_file_id}`,
    type: 'library_file',
    id: file.library_file_id,
    name: file.name ?? '',
    thumbnail: file.thumbnail || null,
    icon: fileIcon(file.file_type),
    typeLabel: (file.file_type || 'File').toUpperCase(),
    detail: file.folder || null,
    badge: null,
    price: null,
    sizeLabel: file.size_label || null,
    sizeBytes: file.size_bytes ?? parseSize(file.size_label),
    updated: null,
    folder: file.folder?.trim() || 'Project Library',
    order,
    addedAt: null,
    categoryOrder: Number.MAX_SAFE_INTEGER,
    floorplan: null,
    floorplanName: null,
  };
}

/** The lot's floor plan as a share item: the Sharing Library item when there is one, else the library file. */
function lotFloorplan(lot: ShareableLot): HorizonShareItemRef | null {
  const plan = lot.floorplan;
  if (plan?.shareable_item_id) {
    return { type: 'document', id: plan.shareable_item_id };
  }
  return plan?.library_file_id ? { type: 'library_file', id: plan.library_file_id } : null;
}

/** A file filed under a floor plan folder ("Floorplans", "Floor Plans", "Floor plan"…): a Sharing Library category or a library folder. */
export function isFloorplanRow(row: ShareRow): boolean {
  return isFileRow(row) && /floor\s*-?\s*plans?/i.test(row.folder);
}

/**
 * The floor plans that go with the selected lots (contract §12.4 `lots[].floorplan`), each once, leaving out any
 * lot whose "Include floor plan" is unticked and any plan the agent also picked as a file.
 */
export function lotFloorplanItems(selected: ShareRow[], excludedLotKeys: ReadonlySet<string>): HorizonShareItemRef[] {
  const picked = new Set(selected.map(row => `${row.type}:${row.id}`));
  const plans = new Map<string, HorizonShareItemRef>();
  selected.forEach(row => {
    const plan = row.type === 'lot' && !excludedLotKeys.has(row.key) ? row.floorplan : null;
    const key = plan ? `${plan.type}:${plan.id}` : '';
    if (plan && !picked.has(key)) {
      plans.set(key, plan);
    }
  });
  return [...plans.values()];
}

/**
 * The footer line, e.g. "1 lot · 1 file selected · 4.2 MB total". A lot's floor plan counts as a file;
 * `sizeOf` gives an item's size when the dialog knows it.
 */
export function shareSelectionText(
  selected: ShareRow[],
  floorplans: HorizonShareItemRef[],
  sizeOf: (item: HorizonShareItemRef) => number | null
): string {
  if (!selected.length) {
    return 'No items selected';
  }
  const lots = selected.filter(row => row.type === 'lot').length;
  const files = selected.length - lots + floorplans.length;
  const parts = [];
  if (lots) {
    parts.push(`${lots} lot${lots === 1 ? '' : 's'}`);
  }
  if (files) {
    parts.push(`${files} file${files === 1 ? '' : 's'}`);
  }
  const bytes = [...selected.map(row => row.sizeBytes ?? 0), ...floorplans.map(plan => sizeOf(plan) ?? 0)].reduce((sum, size) => sum + size, 0);
  return `${parts.join(' · ')} selected${bytes ? ` · ${formatBytes(bytes)} total` : ''}`;
}

export function toLotRow(lot: ShareableLot, order: number): ShareRow {
  const specs = [
    lot.level,
    lot.bed !== null && lot.bed !== undefined ? `${lot.bed} bed` : null,
    lot.bath !== null && lot.bath !== undefined ? `${lot.bath} bath` : null,
    lot.car !== null && lot.car !== undefined ? `${lot.car} car` : null,
    lot.floorplan?.name ? `Plan ${lot.floorplan.name}` : null,
  ];
  return {
    key: `lot:${lot.unit_id}`,
    type: 'lot',
    id: lot.unit_id,
    name: lot.name ?? '',
    thumbnail: lot.image || null,
    icon: 'fa-building',
    typeLabel: 'Lot',
    detail: specs.filter(Boolean).join(' · ') || null,
    badge: lot.availability_label || lot.availability || null,
    price: lot.price ?? null,
    sizeLabel: null,
    sizeBytes: null,
    updated: null,
    folder: 'Properties / Lots',
    order,
    addedAt: null,
    categoryOrder: -1,
    floorplan: lotFloorplan(lot),
    floorplanName: lot.floorplan?.name?.trim() || null,
  };
}
